import React, { useEffect, useMemo, useState } from 'react';
import adicionarUser from '../../../API/Manager/adicionarUserAdm';
import updateUser from '../../../API/Manager/alterarUser';

export default function UserPanel({ mode, user, condominios = [], onClose, onSaved }) {
  const isEdit = mode === 'edit';

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('default');
  const [condominio, setCondominio] = useState('all');
  const [report, setReport] = useState({ sindico: true, administrador: true, construtora: true, cmd: true });

  // só para "create"
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const emailInvalid = email.length > 0 && !validateEmail(email);
  const passwordInvalid = !isEdit && password.length > 0 && !validatePassword(password);
  const confirmMismatch = !isEdit && confirmPassword.length > 0 && password !== confirmPassword;

  // carrega dados no modo edição
  useEffect(() => {
    if (isEdit && user) {
      setFullName(user.name || '');
      setUsername(user.user || '');
      setEmail(user.email || '');
      setRole(user.role || 'default');
      setCondominio(user.condominio || 'all');
      setReport({
        sindico: !!user.report?.sindico,
        administrador: !!user.report?.administrador,
        construtora: !!user.report?.construtora,
        cmd: !!user.report?.cmd || !!user.report?.CMD,
      });
    } else {
      // defaults ao criar
      setFullName('');
      setUsername('');
      setEmail('');
      setRole('default');
      setCondominio('');
      setReport({ sindico: true, administrador: true, construtora: true, cmd: true });
      setPassword('');
      setConfirmPassword('');
      setError('');
    }
  }, [isEdit, user]);

  const canSubmit = useMemo(() => {
    if (!fullName || !username || !email || !condominio) return false;
    if (!validateEmail(email)) return false;
    if (!isEdit) {
      if (!password || !confirmPassword) return false;
      if (password !== confirmPassword) return false;
      if (!validatePassword(password)) return false;
    }
    return true;
  }, [fullName, username, email, condominio, password, confirmPassword, isEdit]);

  const togglePerm = (k) => setReport((prev) => ({ ...prev, [k]: !prev[k] }));

  const onChangeUsername = (e) => {
    let v = e.target.value.replace(/\s/g, '');
    setUsername(v.toLowerCase());
  };

  const handleSubmit = async () => {
    setError('');
    if (!canSubmit) {
      setError('Verifique os campos obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const reportLower = {
        sindico: !!report.sindico,
        administrador: !!report.administrador,
        construtora: !!report.construtora,
        cmd: !!report.cmd,
      };

      if (isEdit) {
        const updated = {
          oldName: user?.name, // se sua API exige
          newName: fullName,
          email,
          report: reportLower,
          role,
          condominio,
        };
        await updateUser(user.id, updated);
      } else {
        const payload = {
          user: username,
          name: fullName,
          email,
          password,
          report: reportLower,
          condominio,
          role,
        };
        await adicionarUser(payload);
      }

      onSaved?.();      // peça para recarregar a lista
      onClose?.();      // fecha painel
    } catch (err) {
      console.error(err);
      setError('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>
      {error && <div style={{color:'#dc2626', fontSize:12}}>{error}</div>}

      <Field label="Nome completo">
        <input value={fullName} onChange={(e)=>setFullName(e.target.value)} />
      </Field>

      <Field label="Usuário">
        <input
          value={username}
          onChange={onChangeUsername}
          disabled={isEdit} // geralmente não muda no edit
          placeholder="sem espaços e minúsculo"
        />
      </Field>

      <Field label="E-mail">
        <input
          type="email"
          value={email}
          onChange={(e)=>setEmail(e.target.value)}
          placeholder="nome@dominio.com"
          aria-invalid={emailInvalid}
          style={emailInvalid ? { borderColor:'#dc2626', outline:'none' } : undefined}
        />
      </Field>
      {emailInvalid && (
        <div style={{color:'#dc2626', fontSize:12}}>
          E-mail inválido. Use um formato como <b>nome@dominio.com</b>.
        </div>
      )}

      {!isEdit && (
        <>
          <Field label="Senha">
            <input 
              type="password" 
              value={password} 
              onChange={(e)=>setPassword(e.target.value)} 
              aria-invalid={passwordInvalid}
              style={passwordInvalid ? { borderColor:'#dc2626', outline:'none' } : undefined}
              />
          </Field>
          {passwordInvalid && (
            <div style={{color:'#dc2626', fontSize:12}}>
              A senha deve ter no mínimo 8 caracteres, com letras maiúsculas, minúsculas e números.
            </div>
          )}

          <Field label="Confirmar senha">
            <input
              type="password"
              value={confirmPassword}
              onChange={(e)=>setConfirmPassword(e.target.value)}
              aria-invalid={confirmMismatch}
              style={confirmMismatch ? { borderColor:'#dc2626', outline:'none' } : undefined}
            />
          </Field>
          {confirmMismatch && (
            <div style={{color:'#dc2626', fontSize:12}}>
              As senhas não coincidem.
            </div>
          )}
          <Hint>Min. 8 caracteres, com maiúscula, minúscula e número.</Hint>
          
        </>
      )}

      <Field label="Permissões de Reporte">
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8}}>
          {['sindico','administrador','construtora','cmd'].map(k=>(
            <label key={k} style={{display:'flex', alignItems:'center', gap:8}}>
              <input type="checkbox" checked={!!report[k]} onChange={()=>togglePerm(k)} />
              {labelize(k)}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Função (role)">
        <select value={role} onChange={(e)=>setRole(e.target.value)}>
          <option value="default">Default</option>
          <option value="admin">Admin</option>
        </select>
      </Field>

      <Field label="Condomínio">
        <select
          value={condominio}
          onChange={(e)=>setCondominio(e.target.value)}
        >
          {condominios.map((c) => (
            <option key={c.id ?? c.name} value={String(c.name).toLowerCase()}>
              {String(c.name).toLowerCase()}
            </option>
          ))}
        </select>
      </Field>

      <div className="user-panel-buttons">
        <button 
          type="button"
          className="up-btn up-btn-ghost"
          onClick={onClose} 
          disabled={saving}>
            Cancelar
          </button>

        <button
          type="button"
          className="up-btn up-btn-primary" 
          onClick={handleSubmit} 
          disabled={saving || !canSubmit}
        >
          {saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Criar usuário')}
        </button>
      </div>
    </div>
  );
}

/* — helpers simples — */
function Field({ label, children }) {
  return (
    <div style={{display:'flex', flexDirection:'column', gap:6}}>
      <label style={{fontWeight:600}}>{label}</label>
      {children}
      <style jsx>{`
        input, select { padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; }
      `}</style>
    </div>
  );
}

function Hint({ children }) {
  return <div style={{fontSize:12, color:'#6b7280'}}>{children}</div>;
}

function labelize(k){ return k.charAt(0).toUpperCase() + k.slice(1); }
function validateEmail(e){ return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
function validatePassword(p){ return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(p); }